import React, { PropsWithChildren } from 'react';
import {
  Takeover,
  TakeoverContent,
  TakeoverFooter,
  TakeoverHeader
} from '../../atoms/takeover-modal/TakeoverModal';
import styles from './full-screen-modal.module.scss';
import { Button } from '../../atoms/button/Button';
import { GHubTheme, useGetBranding, useGetTheme } from '../../../modules/theme';
import { useFeature } from '../../atoms/feature-flags/useFeature';
import { Show } from '../../atoms/show/Show';

type FullScreenModalButtonProps = {
  text: string;
  onClick: () => void;
  icon?: React.ReactNode;
  testId?: string;
};

type FullScreenModalProps = {
  isOpen: boolean;
  onClose: () => void;
  logo?: React.ReactNode;
  primaryButton?: FullScreenModalButtonProps;
  additionalButton?: FullScreenModalButtonProps;
  primaryDownloadPDFButton?: boolean;
};

export const FullScreenModal = ({
  isOpen,
  onClose,
  primaryButton,
  additionalButton,
  children,
  primaryDownloadPDFButton
}: PropsWithChildren<FullScreenModalProps>) => {
  const theme = useGetTheme();
  const { glacierLogoColor, brandLogoColor } = useGetBranding(theme);
  const showDownloadPDFButtons = useFeature('clients.showDownloadPDFButtons');

  const shouldShowPrimaryButton = !primaryDownloadPDFButton || showDownloadPDFButtons;

  const renderLogo = () => (
    <div className={styles.logoContainer}>
      <img alt='Sanlam Glacier' className={styles.topBarIcon} src={glacierLogoColor} />
      {brandLogoColor && (
        <>
          <div className={styles.logoDivider} />
          <img alt='Brand' className={styles.brandLogo} src={brandLogoColor} />
        </>
      )}
    </div>
  );

  return (
    <Takeover open={isOpen} onClose={onClose} className={styles.modal} animated>
      <TakeoverHeader
        hasLogo={false}
        onClose={onClose}
        title={renderLogo()}
        className={styles.closeButton}
      />
      <TakeoverContent containerClassName={styles.content}>
        {children}
        <div className={styles.contentSpacer} />
      </TakeoverContent>
      <TakeoverFooter
        primaryButton={
          primaryButton ? (
            <GHubTheme theme={theme} variant='secondary'>
              <div className={styles.buttonContainer}>
                <Show when={shouldShowPrimaryButton}>
                  <Button
                    datatest-id={primaryButton.testId}
                    startIcon={primaryButton.icon && primaryButton.icon}
                    onClick={primaryButton.onClick}
                    variant={Button.VARIANT.PRIMARY}
                  >
                    {primaryButton.text}
                  </Button>
                </Show>
                {additionalButton && (
                  <Button
                    datatest-id={additionalButton.testId}
                    startIcon={additionalButton.icon && additionalButton.icon}
                    onClick={additionalButton.onClick}
                    variant={Button.VARIANT.PRIMARY}
                  >
                    {additionalButton.text}
                  </Button>
                )}
              </div>
            </GHubTheme>
          ) : null
        }
        secondaryButton={
          <GHubTheme theme={theme} variant='secondary'>
            <Button onClick={onClose} variant={Button.VARIANT.SECONDARY}>
              Close
            </Button>
          </GHubTheme>
        }
      />
    </Takeover>
  );
};
